/**
 * VideoTimelinePreview
 * Mini video player with a draggable timeline scrubber for VideoTrim/VideoSplit nodes.
 *
 * - Trim mode:  two draggable handles (start / end). Seeking the video to each handle position.
 * - Split mode: computed split markers rendered as read-only vertical ticks.
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';

interface VideoTimelinePreviewProps {
  videoUrl: string;
  mode: 'trim' | 'split';

  // Trim mode
  startTime?: number;
  endTime?: number;
  onStartTimeChange?: (t: number) => void;
  onEndTimeChange?: (t: number) => void;

  // Split mode (display-only markers)
  splitMode?: 'equal' | 'seconds';
  numParts?: number;
  segmentDuration?: number;
}

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1);
  return `${m}:${parseFloat(sec) < 10 ? '0' : ''}${sec}`;
}

export const VideoTimelinePreview: React.FC<VideoTimelinePreviewProps> = ({
  videoUrl,
  mode,
  startTime = 0,
  endTime = 5,
  onStartTimeChange,
  onEndTimeChange,
  splitMode = 'equal',
  numParts = 2,
  segmentDuration = 3,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState<'start' | 'end' | null>(null);

  // Seek video to time (clamped)
  const seekTo = useCallback((t: number) => {
    const v = videoRef.current;
    if (v && isFinite(t)) v.currentTime = Math.max(0, Math.min(t, v.duration || 0));
  }, []);

  const handleVideoMeta = useCallback(() => {
    const v = videoRef.current;
    if (v) setDuration(v.duration || 0);
  }, []);

  // Compute pixel position on track for a given time value
  const timeToPercent = (t: number) => (duration > 0 ? Math.max(0, Math.min(1, t / duration)) * 100 : 0);

  // Convert mouse X to time
  const xToTime = useCallback((clientX: number): number => {
    const track = trackRef.current;
    if (!track || duration <= 0) return 0;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging) return;
    const t = xToTime(e.clientX);
    if (dragging === 'start') {
      const clamped = Math.min(t, endTime - 0.1);
      onStartTimeChange?.(parseFloat(clamped.toFixed(1)));
      seekTo(clamped);
    } else {
      const clamped = Math.max(t, startTime + 0.1);
      onEndTimeChange?.(parseFloat(clamped.toFixed(1)));
      seekTo(clamped);
    }
  }, [dragging, xToTime, startTime, endTime, onStartTimeChange, onEndTimeChange, seekTo]);

  const handleMouseUp = useCallback(() => setDragging(null), []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [dragging, handleMouseMove, handleMouseUp]);

  // Compute split marker positions for split mode
  const splitMarkers = React.useMemo(() => {
    if (mode !== 'split' || duration <= 0) return [];
    if (splitMode === 'equal') {
      return Array.from({ length: numParts - 1 }, (_, i) => duration * (i + 1) / numParts);
    }
    const pts: number[] = [];
    let t = segmentDuration;
    while (t < duration) { pts.push(t); t += segmentDuration; }
    return pts;
  }, [mode, splitMode, numParts, segmentDuration, duration]);

  // Track click to seek (trim mode: seek to nearest handle; split mode: no-op)
  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    if (dragging || mode !== 'trim') return;
    const t = xToTime(e.clientX);
    const dStart = Math.abs(t - startTime);
    const dEnd = Math.abs(t - endTime);
    if (dStart < dEnd) {
      onStartTimeChange?.(parseFloat(Math.min(t, endTime - 0.1).toFixed(1)));
      seekTo(t);
    } else {
      onEndTimeChange?.(parseFloat(Math.max(t, startTime + 0.1).toFixed(1)));
      seekTo(t);
    }
  }, [dragging, mode, xToTime, startTime, endTime, onStartTimeChange, onEndTimeChange, seekTo]);

  if (!videoUrl) {
    return (
      <div style={{
        padding: '12px 16px',
        color: 'rgba(255,255,255,0.3)',
        fontSize: '12px',
        textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        Connect a video source to preview
      </div>
    );
  }

  const startPct = timeToPercent(startTime);
  const endPct = timeToPercent(endTime);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      {/* Mini video */}
      <div style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', background: '#111', aspectRatio: '16/9', maxHeight: 120 }}>
        <video
          ref={videoRef}
          src={videoUrl}
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={handleVideoMeta}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
        {duration > 0 && (
          <div style={{ position: 'absolute', bottom: 4, right: 6, fontSize: 10, color: 'rgba(255,255,255,0.6)', fontVariantNumeric: 'tabular-nums', pointerEvents: 'none', background: 'rgba(0,0,0,0.5)', padding: '1px 4px', borderRadius: 3 }}>
            {fmtTime(duration)}
          </div>
        )}
      </div>

      {/* Timeline scrubber */}
      <div style={{ userSelect: 'none' }}>
        {/* Track */}
        <div
          ref={trackRef}
          onClick={handleTrackClick}
          style={{
            position: 'relative',
            height: 20,
            background: 'rgba(255,255,255,0.08)',
            borderRadius: 4,
            cursor: mode === 'trim' ? 'pointer' : 'default',
          }}
        >
          {/* Selected range (trim) */}
          {mode === 'trim' && duration > 0 && (
            <div style={{
              position: 'absolute',
              left: `${startPct}%`,
              width: `${endPct - startPct}%`,
              top: 0, bottom: 0,
              background: 'rgba(99,102,241,0.35)',
              borderRadius: 2,
              pointerEvents: 'none',
            }} />
          )}

          {/* Split markers */}
          {mode === 'split' && splitMarkers.map((t, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${timeToPercent(t)}%`,
                top: 0, bottom: 0,
                width: 2,
                background: 'rgba(99,102,241,0.8)',
                borderRadius: 1,
                transform: 'translateX(-50%)',
                pointerEvents: 'none',
              }}
            />
          ))}

          {/* Start handle */}
          {mode === 'trim' && duration > 0 && (
            <div
              onMouseDown={(e) => { e.stopPropagation(); setDragging('start'); seekTo(startTime); }}
              style={{
                position: 'absolute',
                left: `${startPct}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 14,
                height: 20,
                background: '#6366f1',
                borderRadius: 3,
                cursor: 'col-resize',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ width: 2, height: 10, background: 'rgba(255,255,255,0.6)', borderRadius: 1 }} />
            </div>
          )}

          {/* End handle */}
          {mode === 'trim' && duration > 0 && (
            <div
              onMouseDown={(e) => { e.stopPropagation(); setDragging('end'); seekTo(endTime); }}
              style={{
                position: 'absolute',
                left: `${endPct}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 14,
                height: 20,
                background: '#6366f1',
                borderRadius: 3,
                cursor: 'col-resize',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ width: 2, height: 10, background: 'rgba(255,255,255,0.6)', borderRadius: 1 }} />
            </div>
          )}
        </div>

        {/* Time labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)', fontVariantNumeric: 'tabular-nums' }}>
          {mode === 'trim' && duration > 0 ? (
            <>
              <span style={{ color: '#818cf8' }}>{fmtTime(startTime)}</span>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>{fmtTime(duration)}</span>
              <span style={{ color: '#818cf8' }}>{fmtTime(endTime)}</span>
            </>
          ) : (
            <>
              <span>0:00</span>
              <span>{duration > 0 ? fmtTime(duration) : '--:--'}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoTimelinePreview;
